/**
 * `sibyl triage` — the queue, and the two decisions a person makes about it.
 *
 * ⛔ THIS IS THE GATE. `triage.require_human` defaults true and nothing in this repository posts
 * without passing through here. Accepting and dismissing are the only two verbs, and both are
 * recorded with who and when, because a decision nobody can attribute is a decision that gets
 * re-argued every run.
 *
 * ⛔ A DISMISSAL NEEDS A REASON FROM A FIXED VOCABULARY. Free text would make the dismissal record
 * unusable for the one thing it exists for: telling a false positive apart from a real finding
 * somebody chose not to fix. The vocabulary is aligned to GitHub's code-scanning reasons so it
 * transfers.
 */

import type { SibylConfig } from "../config/schema.js"
import { EXIT } from "../config/validate.js"
import { assertDismissReason, openTriageStore } from "../triage/store.js"
import { DISMISS_REASONS } from "../types/finding.js"
import { renderQueue } from "./queue.js"

export interface TriageCommandOptions {
  readonly config: SibylConfig
  /** Everything after `triage`, so this file owns its own sub-verbs. */
  readonly argv: readonly string[]
  readonly out?: (s: string) => void
  readonly err?: (s: string) => void
  readonly who?: string
}

export async function runTriageCommand(opts: TriageCommandOptions): Promise<number> {
  const out = opts.out ?? ((s: string) => process.stdout.write(s))
  const err = opts.err ?? ((s: string) => process.stderr.write(s))
  const argv = opts.argv
  const flag = (name: string): string | undefined => {
    const i = argv.indexOf(`--${name}`)
    return i === -1 ? undefined : argv[i + 1]
  }
  const has = (name: string): boolean => argv.includes(`--${name}`)
  // ⚠️ `USER` can be unset in a container. Recorded as `unknown` rather than left blank, so the
  // record still says a person decided and does not read as a machine decision.
  // ⭐ `--by` was consumed by the argument parser and read by nothing, so attribution silently
  // fell back to $USER even when a caller named somebody. It is read here now.
  const who = opts.who ?? flag("by") ?? process.env["USER"] ?? "unknown"

  const store = await openTriageStore({ path: opts.config.triage.db })
  try {
    // ⛔ A FLAG'S VALUE IS NOT A POSITIONAL ARGUMENT. This filtered on `!startsWith("--")`, so in
    // `triage dismiss --reason wont_fix abc123` the word `wont_fix` became the TARGET and the real
    // fingerprint was ignored — a dismissal aimed at whatever record happened to match the reason,
    // or at nothing, reported as success. Value-taking flags consume the token after them.
    const VALUE_FLAGS = new Set(["--reason", "--note", "--by"])
    const positional: string[] = []
    for (let i = 0; i < argv.length; i++) {
      const a = argv[i] as string
      if (VALUE_FLAGS.has(a)) { i++; continue }
      if (a.startsWith("--")) continue
      positional.push(a)
    }
    const [verb, target] = positional

    if (verb === undefined || verb === "list") {
      const all = store.all()
      const shown = has("all") ? all : store.todo()
      // ⛔ What the filter hid is stated, not omitted. A queue that quietly shows 3 of 19 reads as a
      // review that found three things.
      const hidden = all.length - shown.length
      out(
        renderQueue(shown, {
          groupBy: opts.config.triage.groupBy,
          excluded:
            hidden > 0
              ? [{ what: "findings", count: hidden, why: "already accepted or dismissed — pass --all to include them" }]
              : [],
        }),
      )
      // ⛔ A queue with findings in it is a successful run. Only a broken store is a failure.
      return EXIT.OK
    }

    if (verb === "accept" || verb === "dismiss") {
      if (target === undefined) {
        err(`⛔ \`sibyl triage ${verb}\` needs a fingerprint. Run \`sibyl triage\` to see them.\n`)
        return EXIT.USAGE
      }
      // ⭐ A prefix is accepted because nobody retypes a full fingerprint, but an AMBIGUOUS prefix is
      // refused rather than resolved to the first match — silently accepting the wrong finding is
      // the one outcome this command must never produce.
      const matches = store.all().filter((r) => r.fingerprint.startsWith(target))
      if (matches.length === 0) {
        err(`⛔ no finding whose fingerprint starts with ${JSON.stringify(target)}\n`)
        return EXIT.USAGE
      }
      if (matches.length > 1) {
        err(`⛔ ${matches.length} findings start with ${JSON.stringify(target)}; use more characters:\n`)
        for (const m of matches) err(`   ${m.fingerprint}\n`)
        return EXIT.USAGE
      }
      const fp = (matches[0] as { fingerprint: string }).fingerprint

      if (verb === "accept") {
        store.accept(fp, who)
        out(`accepted ${fp.slice(0, 12)} — it stays in the queue and becomes postable.\n`)
        return EXIT.OK
      }

      const reason = flag("reason")
      if (reason === undefined) {
        err(`⛔ a dismissal needs --reason, one of: ${DISMISS_REASONS.join(", ")}\n`)
        return EXIT.USAGE
      }
      try {
        assertDismissReason(reason)
      } catch (e) {
        err(`⛔ ${e instanceof Error ? e.message : String(e)}\n`)
        return EXIT.USAGE
      }
      const note = flag("note")
      store.dismiss(fp, { reason, by: who, ...(note === undefined ? {} : { note }) })
      out(`dismissed ${fp.slice(0, 12)} as ${reason} — it will not be raised again while the code stands.\n`)
      return EXIT.OK
    }

    err(`⛔ unknown triage verb ${JSON.stringify(verb)}. Use list, accept or dismiss.\n`)
    return EXIT.USAGE
  } finally {
    store.close()
  }
}
